import React, { useState, useEffect } from 'react'
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { decodeData } from '../../../utils/encodeAndEncode';
import { getTrainees, removeTraineeFromClassroom, getAllUsersExceptClassroom, addTraineesToClassroom, deleteMultipleTrainees, getTraineeGroups, getTraineesInGroup } from '../../../services/adminService';
import { toast } from 'react-toastify';
import { usePermissions } from '../../../context/PermissionsContext';
import AdminPage from '../../../components/admin/AdminPage';
import Loader from '../../../components/common/Loader'
import Nodata from '../../../components/common/NoData'

function ClassroomTrainees() {
    const navigate = useNavigate();
    const { permissions } = usePermissions();
    const { classroomId } = useParams();
    const decodedClassroomId = decodeData(classroomId);
    // console.log('decodedClassroomId', decodedClassroomId);

    // Main data states
    const [trainees, setTrainees] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [traineeToDelete, setTraineeToDelete] = useState(null);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

    // Selection states
    const [selectedTrainees, setSelectedTrainees] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    // Search and pagination states
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);

    // Add trainee modal states
    const [modalSearchTerm, setModalSearchTerm] = useState('');
    const [availableTrainees, setAvailableTrainees] = useState([]);
    const [selectedTraineeIds, setSelectedTraineeIds] = useState(new Set());
    const [modalLoading, setModalLoading] = useState(false);
    const [addMode, setAddMode] = useState('individual'); // 'individual' | 'group'
    const [traineeGroups, setTraineeGroups] = useState([]);
    const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());

    // Loading states
    const [isDeletingTrainee, setIsDeletingTrainee] = useState(false);
    const [isDeletingMultiple, setIsDeletingMultiple] = useState(false);

    // Fetch trainees data
    const fetchTrainees = async () => {
        setLoading(true);
        try {
            const payload = {
                class_id: parseInt(decodedClassroomId),
                page: currentPage,
                limit: itemsPerPage,
                search: searchTerm
            };

            // console.log('Fetching trainees with payload:', payload);

            const response = await getTrainees(payload);
            // console.log('Trainees response:', response);

            if (response.success) {
                setTrainees(response.data.trainees || []);

                // Set pagination data
                const pagination = response.data.pagination || {};
                setTotalPages(pagination.totalPages || 0);
                setTotalRecords(pagination.totalRecords || 0);
            } else {
                toast.error('Failed to fetch trainees data');
                setTrainees([]);
            }
        } catch (error) {
            console.error('Error fetching trainees:', error);
            toast.error('Error loading trainees data');
            setTrainees([]);
        } finally {
            // Add minimum loading time for better UX
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    };

    // Fetch available trainees for the classroom
    const fetchAvailableTrainees = async () => {
        setModalLoading(true);
        try {
            const payload = {
                class_id: parseInt(decodedClassroomId),
                page: 1,
                limit: 1000, // Get a large number to show all available trainees
                search: modalSearchTerm
            };

            // console.log('Fetching available trainees with payload:', payload);

            const response = await getAllUsersExceptClassroom(payload);
            // console.log('Available trainees response:', response);

            if (response.success) {
                // Get the current set of selected IDs
                const selectedIds = selectedTraineeIds;

                // Map the trainees and mark as selected if their ID is in our selectedTraineeIds Set
                const mappedTrainees = response.data.trainees.map(trainee => ({
                    ...trainee,
                    selected: selectedIds.has(trainee.id)
                }));

                setAvailableTrainees(mappedTrainees);
                // console.log('Available trainees mapped with selection preserved:', mappedTrainees);
            } else {
                console.error('Failed to fetch available trainees:', response.message);
                toast.error('Failed to load available trainees');
            }
        } catch (error) {
            console.error('Error fetching available trainees:', error);
            toast.error('Failed to load available trainees');
        } finally {
            setModalLoading(false);
        }
    };

    // Fetch data when component mounts or pagination/search changes
    useEffect(() => {
        fetchTrainees();
    }, [decodedClassroomId, currentPage, itemsPerPage, searchTerm]);

    // Effect to fetch available trainees when search term changes
    useEffect(() => {
        if (showAddModal) {
            const timer = setTimeout(() => {
                fetchAvailableTrainees();
            }, 500); // Debounce search

            return () => clearTimeout(timer);
        }
    }, [modalSearchTerm, showAddModal]);

    const handleSelectAll = (e) => {
        const newSelectAll = e.target.checked;
        setSelectAll(newSelectAll);

        if (newSelectAll) {
            // Select all trainee IDs
            const allTraineeIds = trainees.map(trainee => trainee.id);
            setSelectedTrainees(allTraineeIds);
        } else {
            // Deselect all
            setSelectedTrainees([]);
        }
    };

    const handleSelectTrainee = (traineeId) => {
        setSelectedTrainees(prev => {
            if (prev.includes(traineeId)) {
                // Remove from selection
                setSelectAll(false);
                return prev.filter(id => id !== traineeId);
            } else {
                // Add to selection
                const newSelection = [...prev, traineeId];
                // Check if all rows are now selected
                if (newSelection.length === trainees.length) {
                    setSelectAll(true);
                }
                return newSelection;
            }
        });
    };

    const handleDeleteClick = (trainee) => {
        if (!permissions.classroom_trainees_delete) {
            toast.warning("You don't have permission to delete trainees");
            return;
        }
        setTraineeToDelete(trainee);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!traineeToDelete) return;

        setIsDeletingTrainee(true);
        try {
            const payload = {
                user_id: traineeToDelete.id,
                class_id: parseInt(decodedClassroomId)
            };

            // console.log('Removing trainee with payload:', payload);

            const response = await removeTraineeFromClassroom(payload);

            if (response.success) {
                toast.success('Trainee removed successfully');
                // Refresh the trainees list to get updated data and pagination
                fetchTrainees();
            } else {
                toast.error(response.message || 'Failed to remove trainee');
            }
        } catch (error) {
            console.error('Error removing trainee:', error);
            toast.error('An error occurred while removing trainee');
        } finally {
            setIsDeletingTrainee(false);
            setShowDeleteModal(false);
            setTraineeToDelete(null);
        }
    };

    const handleBulkDeleteClick = () => {
        if (!permissions.classroom_trainees_delete) {
            toast.warning("You don't have permission to delete trainees");
            return;
        }
        if (selectedTrainees.length === 0) {
            toast.warning('Please select trainees to delete');
            return;
        }
        setShowBulkDeleteModal(true);
    };

    const handleConfirmBulkDelete = async () => {
        if (selectedTrainees.length === 0) return;

        setIsDeletingMultiple(true);
        try {
            const payload = {
                user_ids: selectedTrainees,
                class_id: parseInt(decodedClassroomId)
            };

            // console.log('Removing multiple trainees with payload:', payload);

            const response = await deleteMultipleTrainees(payload);

            if (response.success) {
                toast.success(`${selectedTrainees.length} trainees removed successfully`);

                // Clear selection
                setSelectedTrainees([]);
                setSelectAll(false);

                // Refresh the trainees list to get updated data and pagination
                fetchTrainees();
            } else {
                toast.error(response.message || 'Failed to remove trainees');
            }
        } catch (error) {
            console.error('Error removing multiple trainees:', error);
            toast.error('An error occurred while removing trainees');
        } finally {
            setIsDeletingMultiple(false);
            setShowBulkDeleteModal(false);
        }
    };

    // Handle individual trainee selection in modal
    const handleSelectTraineeToAdd = (id) => {
        // Update the UI state
        const updatedTrainees = availableTrainees.map(t =>
            t.id === id ? { ...t, selected: !t.selected } : t
        );
        setAvailableTrainees(updatedTrainees);

        // Update the selected IDs set
        const newSelectedIds = new Set(selectedTraineeIds);
        const trainee = updatedTrainees.find(t => t.id === id);

        if (trainee.selected) {
            newSelectedIds.add(id);
        } else {
            newSelectedIds.delete(id);
        }

        setSelectedTraineeIds(newSelectedIds);
    };

    // Handle selecting all trainees in modal
    const handleSelectAllToAdd = () => {
        if (availableTrainees.every(t => t.selected)) {
            // If all are selected, unselect all
            const updatedTrainees = availableTrainees.map(t => ({ ...t, selected: false }));
            setAvailableTrainees(updatedTrainees);
            setSelectedTraineeIds(new Set());
        } else {
            // Otherwise, select all
            const updatedTrainees = availableTrainees.map(t => ({ ...t, selected: true }));
            setAvailableTrainees(updatedTrainees);

            // Add all trainee IDs to the selected set
            const newSelectedIds = new Set(selectedTraineeIds);
            updatedTrainees.forEach(trainee => {
                newSelectedIds.add(trainee.id);
            });
            setSelectedTraineeIds(newSelectedIds);
        }
    };

    // Handle add trainees submission
    const handleAddSelectedTrainees = async () => {
        let userIds = [];

        if (addMode === 'individual') {
            userIds = Array.from(selectedTraineeIds);
        } else {
            // Fetch trainees from selected groups
            try {
                const groupIds = Array.from(selectedGroupIds);
                const traineePromises = groupIds.map(groupId => 
                    getTraineesInGroup(groupId, { page: 1, limit: 1000 })
                );
                const responses = await Promise.all(traineePromises);
                
                const allTrainees = responses.flatMap(res => 
                    res?.success ? (res.data?.trainees || res.data || []) : []
                );
                
                userIds = [...new Set(allTrainees.map(t => t.id))];
            } catch (error) {
                console.error('Error fetching group trainees:', error);
                toast.error('Failed to fetch trainees from groups');
                return;
            }
        }

        if (userIds.length === 0) {
            toast.warning('Please select trainees or groups to add');
            return;
        }

        const payload = {
            user_ids: userIds,
            class_id: parseInt(decodedClassroomId)
        };

        try {
            const response = await addTraineesToClassroom(payload);

            if (response.success) {
                toast.success('Trainees added successfully');
                handlePopupClose();
                fetchTrainees();
            } else {
                toast.error(response.message || 'Failed to add trainees');
            }
        } catch (error) {
            console.error('Error adding trainees:', error);
            toast.error('An error occurred while adding trainees');
        }
    };

    // Handle popup close
    const handlePopupClose = () => {
        setShowAddModal(false);
        setModalSearchTerm('');
        setAvailableTrainees([]);
        setSelectedTraineeIds(new Set());
        setSelectedGroupIds(new Set());
        setAddMode('individual');
    };

    // Fetch trainee groups
    const fetchTraineeGroups = async () => {
        try {
            const response = await getTraineeGroups();
            if (response?.success) {
                setTraineeGroups(response.data?.groups || response.data || []);
            }
        } catch (error) {
            console.error('Error fetching trainee groups:', error);
        }
    };

    // Open modal and fetch available trainees
    const handleOpenAddTraineeModal = () => {
        if (!permissions.classroom_trainees_create) {
            toast.warning("You don't have permission to add trainees");
            return;
        }
        setShowAddModal(true);
        setModalSearchTerm(""); // Reset search term
        setSelectedTraineeIds(new Set()); // Reset selected trainees
        setSelectedGroupIds(new Set()); // Reset selected groups
        setAddMode('individual'); // Reset to individual mode
        fetchAvailableTrainees();
        fetchTraineeGroups();
    };

    // Handle search input change with debouncing
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to first page on new search
    };

    // Handle items per page change
    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value));
        setCurrentPage(1); // Reset to first page when changing items per page
    };

    // Format date to readable format
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Filter trainees based on search (for modal)
    const filteredTrainees = availableTrainees.filter(trainee =>
        trainee.name.toLowerCase().includes(modalSearchTerm.toLowerCase()) ||
        trainee.email.toLowerCase().includes(modalSearchTerm.toLowerCase())
    );

    const handleBackToDashboard = () => {
        navigate(`/admin/classrooms/classroom-dashboard/${classroomId}`);
    };

    return (
        <>
            <AdminPage
                back={
                    <button
                        type="button"
                        onClick={handleBackToDashboard}
                        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
                        title="Back to Live Session"
                    >
                        <Icon icon="fluent:arrow-left-24-regular" width="20" height="20" className="shrink-0" />
                        <span>Back to Live Session</span>
                    </button>
                }
                title="Trainees"
                description="Manage trainees in this live session. Add or remove trainees."
                actions={
                    <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto sm:flex-nowrap">
                        <div className="relative flex-1 min-w-[180px] max-w-xs">
                            <Icon icon="fluent:search-20-regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="search"
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#4F6BFF]/20 focus:border-[#4F6BFF]"
                                placeholder="Search trainees..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                aria-label="Search trainees"
                            />
                        </div>
                        {selectedTrainees.length > 0 && (
                            <button
                                type="button"
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                onClick={handleBulkDeleteClick}
                                disabled={!permissions.classroom_trainees_delete}
                                title={!permissions.classroom_trainees_delete ? "You don't have permission to delete trainees" : "Delete selected trainees"}
                            >
                                <Icon icon="fluent:delete-24-regular" width="18" height="18" />
                                Delete ({selectedTrainees.length})
                            </button>
                        )}
                        <button
                            type="button"
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                            onClick={handleOpenAddTraineeModal}
                            disabled={!permissions.classroom_trainees_create}
                            title={!permissions.classroom_trainees_create ? "You don't have permission to add trainees" : "Add trainees to classroom"}
                        >
                            <Icon icon="fluent:people-add-24-regular" width="18" height="18" />
                            Add Trainees
                        </button>
                    </div>
                }
            >
                <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                    <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                        <table className="w-full text-sm" style={{ minWidth: '700px' }}>
                            <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                                <tr>
                                    <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-12">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 text-[#4F6BFF] border-gray-300 rounded focus:ring-[#4F6BFF]/20"
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                            aria-label="Select all"
                                        />
                                    </th>
                                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-16">#</th>
                                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Trainee</th>
                                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Mobile</th>
                                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-32">Joined</th>
                                    <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loading ? (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-16">
                                            <Loader caption="Loading trainees..." />
                                        </td>
                                    </tr>
                                ) : trainees.length > 0 ? (
                                    trainees.map((trainee, index) => (
                                        <tr key={trainee.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-3 align-middle">
                                                <div className="flex justify-center">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 text-[#4F6BFF] border-gray-300 rounded focus:ring-[#4F6BFF]/20"
                                                        checked={selectedTrainees.includes(trainee.id)}
                                                        onChange={() => handleSelectTrainee(trainee.id)}
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 align-middle text-gray-600">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                            <td className="px-4 py-3 align-middle">
                                                <div className="flex items-center gap-3">
                                                    {trainee.profile_pic_url ? (
                                                        <img
                                                            src={trainee.profile_pic_url}
                                                            alt={trainee.name}
                                                            className="rounded-full w-9 h-9 object-cover border border-gray-100"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                if (e.target.nextElementSibling) e.target.nextElementSibling.classList.remove('hidden');
                                                            }}
                                                        />
                                                    ) : null}
                                                    <span className={`flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-gray-500 shrink-0 ${trainee.profile_pic_url ? 'hidden' : ''}`}>
                                                        <Icon icon="fluent:person-24-regular" width="20" height="20" />
                                                    </span>
                                                    <span className="font-medium text-gray-900">{trainee.name || 'N/A'}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 align-middle text-gray-600">{trainee.email || '–'}</td>
                                            <td className="px-4 py-3 align-middle text-gray-600">{trainee.mobile || '–'}</td>
                                            <td className="px-4 py-3 align-middle text-gray-600">{formatDate(trainee.joined_at)}</td>
                                            <td className="px-4 py-3 align-middle">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        type="button"
                                                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title={!permissions.classroom_trainees_delete ? "You don't have permission to delete trainees" : "Remove trainee"}
                                                        onClick={() => handleDeleteClick(trainee)}
                                                        disabled={!permissions.classroom_trainees_delete}
                                                    >
                                                        <Icon icon="fluent:delete-24-regular" width="18" height="18" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-16">
                                            <div className="flex flex-col items-center gap-4">
                                                <Nodata caption="No trainees found in this classroom" />
                                                <button
                                                    type="button"
                                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                    onClick={handleOpenAddTraineeModal}
                                                    disabled={!permissions.classroom_trainees_create}
                                                    title={!permissions.classroom_trainees_create ? "You don't have permission to add trainees" : "Add trainees to classroom"}
                                                >
                                                    <Icon icon="fluent:people-add-24-regular" width="18" height="18" />
                                                    Add Trainees
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                        <span className="hidden sm:inline">Rows per page:</span>
                        <select
                            className="px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F6BFF]/20 text-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                        <span className="text-gray-500">
                            {totalRecords === 0 ? 'No records' : `Showing ${(currentPage - 1) * itemsPerPage + 1}–${Math.min(currentPage * itemsPerPage, totalRecords)} of ${totalRecords}`}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                        <button
                            type="button"
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1 || totalPages === 0}
                            title="Previous page"
                        >
                            <Icon icon="fluent:chevron-left-24-regular" width={16} height={16} />
                        </button>
                        <span className="px-2 text-gray-700">
                            Page <span className="font-medium">{totalPages === 0 ? 0 : currentPage}</span> of <span className="font-medium">{totalPages || 0}</span>
                        </span>
                        <button
                            type="button"
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages || totalPages === 0}
                            title="Next page"
                        >
                            <Icon icon="fluent:chevron-right-24-regular" width={16} height={16} />
                        </button>
                    </div>
                </div>
            </AdminPage>

            {/* Single Delete Confirmation Modal */}
            {showDeleteModal && traineeToDelete && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="font-semibold text-lg text-gray-900 m-0">Remove trainee</h3>
                            <button
                                type="button"
                                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setTraineeToDelete(null);
                                }}
                            >
                                <Icon icon="mdi:close" width="20" height="20" />
                            </button>
                        </div>
                        <div className="p-6">
                                <p>Are you sure you want to remove this trainee?</p>
                                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg mt-3">
                                    <div className="flex items-center mb-2">
                                        <Icon icon="fluent:person-24-regular" className="mr-2" width="24" height="24" />
                                        <strong>{traineeToDelete.name}</strong>
                                    </div>
                                    <div className="text-gray-600">{traineeToDelete.email}</div>
                                </div>
                            </div>
                            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                                <button
                                    type="button"
                                    className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-100"
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setTraineeToDelete(null);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium disabled:opacity-50"
                                    onClick={handleConfirmDelete}
                                    disabled={isDeletingTrainee}
                                >
                                    {isDeletingTrainee ? (
                                        <>
                                            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                            Removing...
                                        </>
                                    ) : 'Remove trainee'}
                                </button>
                            </div>
                        </div>
                    </div>
            )}

            {/* Bulk Delete Confirmation Modal */}
            {showBulkDeleteModal && selectedTrainees.length > 0 && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="font-semibold text-lg text-gray-900 m-0">Remove selected trainees</h3>
                            <button
                                type="button"
                                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                onClick={() => setShowBulkDeleteModal(false)}
                            >
                                <Icon icon="mdi:close" width="20" height="20" />
                            </button>
                        </div>
                        <div className="p-6">
                            <p className="text-gray-700 mb-0">Are you sure you want to remove {selectedTrainees.length} selected trainee{selectedTrainees.length !== 1 ? 's' : ''}?</p>
                            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl mt-4 flex items-center gap-2">
                                <Icon icon="fluent:warning-24-regular" width="22" height="22" className="shrink-0" />
                                <span>This action cannot be undone.</span>
                            </div>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                            <button
                                type="button"
                                className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-100"
                                onClick={() => setShowBulkDeleteModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium disabled:opacity-50"
                                onClick={handleConfirmBulkDelete}
                                disabled={isDeletingMultiple}
                            >
                                {isDeletingMultiple ? (
                                    <>
                                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                        Removing...
                                    </>
                                ) : `Remove ${selectedTrainees.length} trainee${selectedTrainees.length !== 1 ? 's' : ''}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Trainee Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center shrink-0">
                            <h3 className="text-lg font-semibold text-gray-900 m-0">Add Trainees</h3>
                            <button
                                type="button"
                                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                                onClick={handlePopupClose}
                                aria-label="Close"
                            >
                                <Icon icon="mdi:close" width="22" height="22" />
                            </button>
                        </div>
                        <div className="px-6 py-3 border-b border-gray-100 flex gap-2">
                            <button
                                type="button"
                                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                                    addMode === 'individual'
                                        ? 'bg-[#4F6BFF] text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                                onClick={() => setAddMode('individual')}
                            >
                                Individual
                            </button>
                            <button
                                type="button"
                                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                                    addMode === 'group'
                                        ? 'bg-[#4F6BFF] text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                                onClick={() => setAddMode('group')}
                            >
                                By Group
                            </button>
                        </div>
                        <div className="p-6 flex flex-col min-h-0">
                            {addMode === 'individual' ? (
                                <>
                                    <div className="relative mb-4">
                                        <Icon icon="fluent:search-20-regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                        <input
                                            type="search"
                                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-[#4F6BFF]/20 focus:border-[#4F6BFF] outline-none text-sm"
                                            placeholder="Search by name or email..."
                                            value={modalSearchTerm}
                                            onChange={(e) => setModalSearchTerm(e.target.value)}
                                            disabled={modalLoading}
                                        />
                                    </div>
                                    <div className="flex justify-between items-center mb-3">
                                        <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700">
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 text-[#4F6BFF] border-gray-300 rounded focus:ring-[#4F6BFF]/20"
                                                checked={availableTrainees.length > 0 && availableTrainees.every(t => t.selected)}
                                                onChange={handleSelectAllToAdd}
                                            />
                                            Select all
                                        </label>
                                        <span className="text-sm text-gray-500">
                                            {selectedTraineeIds.size} selected
                                        </span>
                                    </div>
                                    <div className="border border-gray-200 rounded-xl overflow-hidden flex-1 min-h-[200px] max-h-[320px] overflow-y-auto bg-gray-50/30">
                                        {modalLoading ? (
                                            <div className="flex items-center justify-center min-h-[200px]">
                                                <Loader caption="Loading available trainees..." />
                                            </div>
                                        ) : filteredTrainees.length > 0 ? (
                                            <ul className="p-2 space-y-1">
                                                {filteredTrainees.map(trainee => (
                                                    <li
                                                        key={trainee.id}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => handleSelectTraineeToAdd(trainee.id)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelectTraineeToAdd(trainee.id); } }}
                                                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                                                            trainee.selected
                                                                ? 'bg-[#4F6BFF]/10 border-[#4F6BFF]/30'
                                                                : 'bg-white border-gray-100 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className="w-4 h-4 text-[#4F6BFF] border-gray-300 rounded focus:ring-[#4F6BFF]/20 shrink-0"
                                                            checked={trainee.selected}
                                                            onChange={() => handleSelectTraineeToAdd(trainee.id)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        {trainee.profile_pic_url ? (
                                                            <img
                                                                src={trainee.profile_pic_url}
                                                                alt=""
                                                                className="rounded-full w-10 h-10 object-cover border border-gray-100 shrink-0"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    if (e.target.nextElementSibling) e.target.nextElementSibling.classList.remove('hidden');
                                                                }}
                                                            />
                                                        ) : null}
                                                        <span className={`flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-500 shrink-0 ${trainee.profile_pic_url ? 'hidden' : ''}`}>
                                                            <Icon icon="fluent:person-24-regular" width="22" height="22" />
                                                        </span>
                                                        <div className="min-w-0">
                                                            <div className="font-medium text-gray-900 truncate">{trainee.name}</div>
                                                            <div className="text-sm text-gray-500 truncate">{trainee.email}</div>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="flex items-center justify-center min-h-[200px]">
                                                <Nodata caption="No trainees available to add" />
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="flex justify-between items-center mb-3">
                                        <span className="text-sm font-medium text-gray-700">Select Groups</span>
                                        <span className="text-sm text-gray-500">
                                            {selectedGroupIds.size} selected
                                        </span>
                                    </div>
                                    <div className="border border-gray-200 rounded-xl overflow-hidden flex-1 min-h-[200px] max-h-[380px] overflow-y-auto bg-gray-50/30">
                                        {traineeGroups.length > 0 ? (
                                            <ul className="p-2 space-y-1">
                                                {traineeGroups.map(group => (
                                                    <li
                                                        key={group.id}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => {
                                                            setSelectedGroupIds(prev => {
                                                                const newSet = new Set(prev);
                                                                if (newSet.has(group.id)) {
                                                                    newSet.delete(group.id);
                                                                } else {
                                                                    newSet.add(group.id);
                                                                }
                                                                return newSet;
                                                            });
                                                        }}
                                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); } }}
                                                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                                                            selectedGroupIds.has(group.id)
                                                                ? 'bg-[#4F6BFF]/10 border-[#4F6BFF]/30'
                                                                : 'bg-white border-gray-100 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className="w-4 h-4 text-[#4F6BFF] border-gray-300 rounded focus:ring-[#4F6BFF]/20 shrink-0"
                                                            checked={selectedGroupIds.has(group.id)}
                                                            onChange={() => {
                                                                setSelectedGroupIds(prev => {
                                                                    const newSet = new Set(prev);
                                                                    if (newSet.has(group.id)) {
                                                                        newSet.delete(group.id);
                                                                    } else {
                                                                        newSet.add(group.id);
                                                                    }
                                                                    return newSet;
                                                                });
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <span className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-500 shrink-0">
                                                            <Icon icon="fluent:people-team-24-regular" width="22" height="22" />
                                                        </span>
                                                        <div className="min-w-0">
                                                            <div className="font-medium text-gray-900 truncate">{group.name}</div>
                                                            {group.description && (
                                                                <div className="text-sm text-gray-500 truncate">{group.description}</div>
                                                            )}
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="flex items-center justify-center min-h-[200px]">
                                                <Nodata caption="No trainee groups available" />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 shrink-0 bg-gray-50/50">
                            <button
                                type="button"
                                className="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-100 transition-colors"
                                onClick={handlePopupClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                onClick={handleAddSelectedTrainees}
                                disabled={addMode === 'individual' ? selectedTraineeIds.size === 0 : selectedGroupIds.size === 0}
                            >
                                {addMode === 'individual' 
                                    ? `Add ${selectedTraineeIds.size > 0 ? `${selectedTraineeIds.size} ` : ''}Trainee${selectedTraineeIds.size !== 1 ? 's' : ''}`
                                    : `Add ${selectedGroupIds.size > 0 ? `${selectedGroupIds.size} ` : ''}Group${selectedGroupIds.size !== 1 ? 's' : ''}`
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default ClassroomTrainees

